import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Box, Center, Flex, Text } from '@chakra-ui/react';

import type { ApiNotification } from '@izimi/api';
import { useModules } from '@izimi/modules';

import type { CustomIconName } from '../icon';
import { CustomIcon } from '../icon';
import { Spinner } from '../Spinner/Spinner';
import { notificationActionItems } from './actions';
import NotificationItem from './NotificationItem';
import { NotificationPopOverBodyWrapper } from './NotificationPopOverBodyWrapper';
import { UnsupportedNotification } from './UnsupportedNotification';

type Props<Routes extends Record<string, string>> = {
  routes: Routes;
};

export const NotificationPopOverBody = <Routes extends Record<string, string>>({
  routes,
}: Props<Routes>) => {
  const { t } = useTranslation('notifications');
  const {
    notifications: { usePagedNotifications },
  } = useModules();
  const {
    getNotificationItems,
    notifications,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    hasNextPage,
  } = usePagedNotifications({ pageSize: 10 });

  const notificationItems = getNotificationItems<CustomIconName>();

  const renderItem = useCallback(
    ({ type, ...notification }: ApiNotification) => {
      const notificationItem = notificationItems[type];

      if (notificationItem) {
        return (
          <NotificationItem
            key={notification.id}
            notification={notification}
            Icon={<CustomIcon {...notificationItems[type].icon} />}
            messageValues={
              // @ts-expect-error too complex for ts
              notificationItems[type].messageValues(notification)
            }
            messageKey={notificationItems[type].messageKey}
            messageComponents={
              notificationActionItems[type]?.messageComponents // @ts-expect-error too complex for ts
                ? notificationActionItems[type]?.messageComponents<Routes>(notification, routes)
                : undefined
            }
            onDeleteNotification={refetch}>
            {notificationActionItems[type]?.actions // @ts-expect-error too complex for ts
              ? notificationActionItems[type]?.actions(notification)
              : null}
          </NotificationItem>
        );
      }
      return <UnsupportedNotification type={type} key={notification.id} />;
    },
    [notificationItems, refetch, routes],
  );

  const endReached = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const [infiniteRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    onLoadMore: endReached,
    rootMargin: '0px 0px 400px 0px',
  });

  if (!notifications?.length) {
    return (
      <NotificationPopOverBodyWrapper bodyOverflow='hidden'>
        <Center py={6}>{isLoading ? <Spinner /> : <Text>{t('notifications:empty')}</Text>}</Center>
      </NotificationPopOverBodyWrapper>
    );
  }

  return (
    <NotificationPopOverBodyWrapper bodyOverflow='scroll'>
      {notifications && notifications?.length > 0 && notifications.map(renderItem)}
      <Flex justifyContent='center'>
        {isFetchingNextPage && <Spinner />}
        {!isFetchingNextPage && !isLoading && <Box ref={infiniteRef} />}
      </Flex>
    </NotificationPopOverBodyWrapper>
  );
};
